import { unwrap } from "@sagot/api-client";
import { isReservedSlug } from "@sagot/constants";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useApi } from "../session/session-provider";
import { queryKeys } from "./query-keys";

const SLUG_MAX_LENGTH = 40;
export const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])$/;
const DEBOUNCE_MS = 350;

/** Client preview of the workspace slug (the server makes the final, unique one). */
export function slugify(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, SLUG_MAX_LENGTH)
    .replace(/-+$/g, "");
}

export function useDebounced<T>(value: T, delayMs = DEBOUNCE_MS): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(value);
    }, delayMs);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delayMs]);
  return debounced;
}

export type SlugStatus = "idle" | "checking" | "available" | "taken" | "reserved";

/** `GET /v1/workspaces/slug-available` (public, rate-limited), debounced while typing. */
export function useSlugAvailability(slug: string): SlugStatus {
  const api = useApi();
  const debounced = useDebounced(slug);
  const valid = SLUG_PATTERN.test(debounced);
  const query = useQuery({
    queryKey: queryKeys.slug(debounced),
    queryFn: () => unwrap(api.GET("/v1/workspaces/slug-available", { params: { query: { slug: debounced } } })),
    enabled: valid && !isReservedSlug(debounced),
    staleTime: 30_000,
    retry: false,
  });
  if (!slug || !SLUG_PATTERN.test(slug)) return "idle";
  if (isReservedSlug(slug)) return "reserved";
  if (debounced !== slug || query.isPending) return "checking";
  if (query.isError) return "idle";
  return query.data.available ? "available" : "taken";
}
